// src/pages/orderAssignment/components/BulkStatusUpdateModal.jsx
import React, { useState } from "react";
import { X, CheckCircle, AlertCircle, Calendar } from "lucide-react";
import { useLocalization } from "../../../contexts/LocalizationContext";
import styles from "./BulkStatusUpdateModal.module.css";

const CITY_UNKNOWN_ID = 9999;

const BulkStatusUpdateModal = ({
  selectedOrderIds,
  selectedOrders,
  availableStatuses,
  orderStatusReasons,
  onClose,
  onUpdate,
}) => {
  const { t } = useLocalization();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [holdUntilDate, setHoldUntilDate] = useState("");
  const [error, setError] = useState("");

  const selectedStatusObj = availableStatuses.find(
    (s) => s.key === selectedStatus
  );
  const requiresReason = selectedStatusObj?.requiresReason;
  const requiresHoldDate = selectedStatusObj?.requiresHoldDate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (selectedStatus === 'confirmed') {
      const ordersWithUnknownCity = selectedOrders.filter(order => 
        order.cityId === CITY_UNKNOWN_ID || order.City?.id === CITY_UNKNOWN_ID
      );

      if (ordersWithUnknownCity.length > 0) {
        const orderNumbers = ordersWithUnknownCity.map(o => o.orderNumber).join(', ');
        setError(
          `Cannot confirm orders with incomplete address. ` +
          `The following orders have "City Unknown": ${orderNumbers}. ` +
          `Please update their addresses first.`
        );
        return;
      }
    }

    // Validation
    if (!selectedStatus) {
      setError(t("orderAssignment.pleaseSelectStatus"));
      return;
    }

    if (requiresReason && !selectedReason) {
      setError(t("orderAssignment.pleaseSelectReason"));
      return;
    }

    if (requiresHoldDate && !holdUntilDate) {
      setError(t("orderAssignment.pleaseSelectHoldDate"));
      return;
    }

    setIsUpdating(true);
    try {
      const updateData = {
        orderStatusKey: selectedStatus,
        orderStatusReasonKey: requiresReason ? selectedReason : null,
        holdUntilDate: requiresHoldDate ? holdUntilDate : null,
      };

      await onUpdate(updateData);
    } catch (err) {
      setError(err.message || t("orderAssignment.failedToUpdateOrders"));
      setIsUpdating(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{t("orderAssignment.bulkUpdateStatus")}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.infoBox}>
            <CheckCircle size={20} />
            <div>
              <p className={styles.infoTitle}>
                {t("orderAssignment.aboutToUpdateOrders", {
                  count: selectedOrderIds.length,
                })}
              </p>
              <p className={styles.infoText}>
                {t("orderAssignment.bulkUpdateDescription")}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={styles.updateForm}>
            <div className={styles.formGroup}>
              <label>{t("orderAssignment.newStatus")} *</label>
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setSelectedReason("");
                  setHoldUntilDate("");
                }}
                className={styles.formSelect}
                required
              >
                <option value="">{t("orderAssignment.selectStatus")}</option>
                {availableStatuses.map((status) => (
                  <option key={status.key} value={status.key}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {requiresReason && orderStatusReasons[selectedStatus] && (
              <div className={styles.formGroup}>
                <label>{t("orderAssignment.statusReason")} *</label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className={styles.formSelect}
                  required
                >
                  <option value="">{t("orderAssignment.selectReason")}</option>
                  {orderStatusReasons[selectedStatus].map((reason) => (
                    <option key={reason.key} value={reason.key}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {requiresHoldDate && (
              <div className={styles.formGroup}>
                <label>
                  <Calendar size={14} />
                  {t("orderAssignment.holdUntilDate")} *
                </label>
                <input
                  type="date"
                  value={holdUntilDate}
                  onChange={(e) => setHoldUntilDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className={styles.formInput}
                  required
                />
              </div>
            )}

            {error && (
              <div className={styles.errorMessage}>
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={onClose}
                className={styles.cancelButton}
                disabled={isUpdating}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className={styles.updateButton}
                disabled={isUpdating}
                style={{
                  backgroundColor: selectedStatusObj?.colorCode || "#7F5CFF",
                }}
              >
                {isUpdating
                  ? t("orderAssignment.updating")
                  : t("orderAssignment.updateOrdersCount", {
                      count: selectedOrderIds.length,
                    })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkStatusUpdateModal;
